import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-f2d11680"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "scroll-list-wrapper relative" }
const _hoisted_2 = ["id"]
const _hoisted_3 = { class: "block text-center" }
const _hoisted_4 = {
  key: 0,
  class: "block"
}
const _hoisted_5 = ["src"]

import { computed, onUpdated, ref, toRefs, inject, onBeforeMount } from "vue";
import { get, set, useScroll, useDateFormat, useArrayReduce, useArrayFindIndex } from "@vueuse/core";
import { DirectMessageServices } from "@/clinic/services";
import { IDirectMessage } from "@/clinic/typings";
import {
  MESSAGE_FIRST_PAGINATOR_ROWS,
  MESSAGE_PAGINATOR_ROWS,
  DATE_ISO_FORMAT,
} from "@/clinic/typings/constants/direct-message";
import MessageTimeAgo from "../common/message/time-ago.vue";
import { ILineAccount } from "@/customer/typings";
import { useStore } from "vuex";
import Avatar from "primevue/avatar";
import MessageSpinner from "../common/spinner.vue";
import MessageContent from "../common/message/content/index.vue";
import { AxiosError } from "axios";
import Toast from "primevue/toast";
import { useToast } from "primevue/usetoast";
import { notityErrorMsg } from "@/clinic/utils/notify-utils";
import { ERROR_LOADING_MESSAGE } from "@/common/constants/message.constants";

type IMessageList = {
  account: ILineAccount;
  line_user_id: number;
};


export default /*#__PURE__*/_defineComponent({
  __name: 'index',
  props: {
    account: null,
    line_user_id: null
  },
  setup(__props: any, { expose }) {

const props = __props as {
  account: ILineAccount;
  line_user_id: number;
};

const store = useStore();



const scrollMessage = ref<HTMLElement>();
const boxMessageSpinner = ref<HTMLElement>();
const boxMoreMessageSpinner = ref<HTMLElement>();
const smooth = ref(false);
const behavior = computed(() => (smooth.value ? "smooth" : "auto"));
const onScroll = () => {
  if (get(top) && get(loadedMore)) loadMessages();
};
const { arrivedState } = useScroll(scrollMessage, { behavior, onScroll });
const { top } = toRefs(arrivedState);
const messages = ref<IDirectMessage[]>([]);
const clinicId = ref(0);
const page = ref(0);
const lineAccount = ref<ILineAccount>({} as ILineAccount);
const toast = useToast();

const setLineAccount = (account: ILineAccount) => set(lineAccount, account);

const setClinicId = async () => set(clinicId, await inject("clinicId"));

const loadedMore = ref(true);

const enableLoadMore = () => set(loadedMore, true);

const disableLoadMore = () => set(loadedMore, false);

const clearMessages = () => set(messages, []);

const resetPaginator = () => set(page, 0);

const loadMessage = async messageId => {
  let message = get(messages).find(message => message.id == messageId);

  while (message == undefined) {
    await loadMessages();
    message = get(messages).find(message => message.id == messageId);
  }
};

const loadMessages = async () => {
  const spinner = get(page) == 0 ? boxMessageSpinner : boxMoreMessageSpinner;
  get(spinner)?.show();
  const params = {
    line_user_id: props.account?.line_user_id || get(lineAccount).line_user_id,
    clinic_id: get(await get(clinicId)),
    page: get(page),
    rows: get(page) == 0 ? MESSAGE_FIRST_PAGINATOR_ROWS : MESSAGE_PAGINATOR_ROWS,
  };
  await DirectMessageServices.getMessages(
    params,
    ({
      live_chat_messages,
      meta: { current_page, total_count },
    }: {
      live_chat_messages: IDirectMessage[];
      meta: {
        current_page: number;
        next_page: number;
        prev_page: number;
        total_count: number;
        total_pages: number;
      };
    }) => {
      if (live_chat_messages.length > 0) {
        live_chat_messages.forEach((message: IDirectMessage) => {
          const index = get(useArrayFindIndex(messages, msg => msg.id == message.id));
          if (index < 0) {
            messages.value.push(message);
          }
        });
        get(messages).length == total_count ? disableLoadMore() : set(page, get(page) == 0 ? 2 : current_page);
      }
    },
    () => {
      notityErrorMsg(toast, ERROR_LOADING_MESSAGE);
    }
  );
  get(spinner)?.hide();
};

const appendMessage = (message: IDirectMessage) => {
  if (!message) return;

  const index = get(useArrayFindIndex(messages, msg => msg.id == message.id));
  if (index < 0) {
    set(messages, [message, ...get(messages)]);
    scrollBottom();
  }
};

const messagesHeight = () => {
  return get(
    useArrayReduce(Array.from(get(scrollMessage)?.childNodes || []), (sum, node) => sum + (node?.offsetHeight || 0), 0)
  );
};

const scrollBottom = () => {
  setTimeout(() => {
    const boxMessage = get(scrollMessage);
    if (boxMessage) {
      boxMessage.scrollTop = messagesHeight();
    }
  }, 500);
};

const refreshMessages = async () => {
  get(boxMessageSpinner)?.show();
  await clearMessages();
  await resetPaginator();
  await loadMessages();
};

const senderAvatar = (currentUserImage: string, senderType: string) => {
  if (senderType == "from_customer") {
    return currentUserImage;
  } else {
    const clinic = store.getters["authClient/selectedClinic"];
    return clinic.logo_url;
  }
};

const messageType = (message: IDirectMessage) => {
  if (message.message_type === "flex") {
    return "flex_message";
  }
  return message.message_type;
};

const hasLoadedMessage = () => get(boxMessageSpinner)?.getVisible() | get(boxMoreMessageSpinner)?.getVisible();

onBeforeMount(async () => {
  await setClinicId();
  refreshMessages();
  get(boxMoreMessageSpinner)?.hide();
  if (props.line_user_id) {
    document.getElementById(props.line_user_id)?.scrollIntoView();
  }
});

onUpdated(() => {
  if (props.account?.id != get(lineAccount)?.id) set(lineAccount, props.account);
});

// eslint-disable-next-line no-undef
expose({
  setLineAccount,
  refreshMessages,
  loadMessages,
  appendMessage,
  hasLoadedMessage,
  enableLoadMore,
  loadMessage,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      (messages.value.length > 0)
        ? (_openBlock(), _createBlock(MessageSpinner, {
            key: 0,
            ref_key: "boxMessageSpinner",
            ref: boxMessageSpinner,
            hasFull: false
          }, null, 512))
        : _createCommentVNode("", true),
      _createVNode(MessageSpinner, {
        ref_key: "boxMoreMessageSpinner",
        ref: boxMoreMessageSpinner,
        hasFull: false,
        loadMore: true
      }, null, 512),
      _createElementVNode("div", {
        ref_key: "scrollMessage",
        ref: scrollMessage,
        class: _normalizeClass(["scroll-list", !_unref(get)(boxMessageSpinner.value)?.getVisible() ? '' : 'hidden'])
      }, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(messages.value, (message, index) => {
          return (_openBlock(), _createElementBlock("div", {
            key: index,
            class: _normalizeClass(["message-wrapper", messageType(message)]),
            id: `message-${message.id}`
          }, [
            _createElementVNode("div", _hoisted_3, [
              (
              !messages.value[index + 1]?.created_at ||
              _unref(get)(_unref(useDateFormat)(messages.value[index + 1]?.created_at, _unref(DATE_ISO_FORMAT))) !=
                _unref(get)(_unref(useDateFormat)(message?.created_at, _unref(DATE_ISO_FORMAT)))
            )
                ? (_openBlock(), _createBlock(MessageTimeAgo, {
                    key: 0,
                    date: message?.created_at
                  }, null, 8, ["date"]))
                : _createCommentVNode("", true),
              _createElementVNode("div", {
                class: _normalizeClass(["flex align-items-center message-body", {
              'flex-row-reverse from_clinic': message?.sender_type == 'from_clinic',
            }])
              }, [
                (message?.sender_type != 'from_clinic')
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      (senderAvatar(__props.account.avatar_url, message?.sender_type))
                        ? (_openBlock(), _createElementBlock("img", {
                            key: 0,
                            class: "user-avatar border-circle",
                            src: senderAvatar(__props.account.avatar_url, message?.sender_type),
                            alt: "user-avatar"
                          }, null, 8, _hoisted_5))
                        : (_openBlock(), _createBlock(_unref(Avatar), {
                            key: 1,
                            icon: "pi pi-user",
                            class: "m-2",
                            size: "large",
                            shape: "circle"
                          }))
                    ]))
                  : _createCommentVNode("", true),
                _createVNode(MessageContent, { message: message }, null, 8, ["message"])
              ], 2)
            ])
          ], 10, _hoisted_2))
        }), 128))
      ], 2)
    ]),
    _createVNode(_unref(Toast), { position: "top-center" })
  ], 64))
}
}

})